/**
 * Session recording/transcript pipeline status chip shown beside the progress note
 * title after the session ends and while the note is still unsigned. Chime appointments show
 * the design Decision 34 statuses; other appointments show none. Hidden before session end (idle).
 */
import { Badge, Group, Text } from '@mantine/core';
import { IconAlertCircle, IconCheck, IconClock } from '@tabler/icons-react';
import type { Appointment, QuestionnaireResponse } from '@medplum/fhirtypes';

import {
  hasNewTranscriptPart,
  isChimeTranscriptAppointment,
  resolveChimeTranscriptUiState,
  type ChimeTranscriptUiState,
} from '@/lib/aiNoteGenerationService';

export interface TranscriptStatusBadgeProps {
  appointment?: Appointment;
  sessionEnded: boolean;
  /** The current note, for the "new part of the transcript" prompt on a Chime draft. */
  note?: QuestionnaireResponse;
}

export const NEW_TRANSCRIPT_PART_TEXT = 'A new part of the transcript is available';
export const TRANSCRIPTION_FAILED_TEXT = 'Transcription failed, not available';

const CHIME_BADGES: Record<
  Exclude<ChimeTranscriptUiState['state'], 'idle'>,
  { label: string; color: string; icon: 'check' | 'clock' | 'alert' }
> = {
  processing: { label: 'Processing', color: 'yellow', icon: 'clock' },
  ready: { label: 'Ready', color: 'green', icon: 'check' },
  drafting: { label: 'Drafting', color: 'yellow', icon: 'clock' },
  failed: { label: 'Failed', color: 'red', icon: 'alert' },
  'not-recorded': { label: 'Not recorded', color: 'gray', icon: 'alert' },
  deleted: { label: 'Deleted', color: 'gray', icon: 'alert' },
};

/** Badge text for a Chime state; Not recorded names the reason when it is known. */
export function chimeTranscriptBadgeLabel(ui: ChimeTranscriptUiState): string | undefined {
  if (ui.state === 'idle') return undefined;
  const { label } = CHIME_BADGES[ui.state];
  return ui.state === 'not-recorded' && ui.noConsent ? `${label}: no consent` : label;
}

function ChimeTranscriptStatusBadge({
  appointment,
  sessionEnded,
  note,
}: TranscriptStatusBadgeProps & { appointment: Appointment }): JSX.Element | null {
  const ui = resolveChimeTranscriptUiState({ appointment, sessionEnded });
  const label = chimeTranscriptBadgeLabel(ui);
  if (ui.state === 'idle' || !label) return null;
  const { color, icon } = CHIME_BADGES[ui.state];
  const Icon = icon === 'check' ? IconCheck : icon === 'clock' ? IconClock : IconAlertCircle;
  const badge = (
    <Badge size="sm" variant="light" color={color} leftSection={<Icon size={10} />}>
      {label}
    </Badge>
  );
  const detail =
    ui.state === 'failed'
      ? TRANSCRIPTION_FAILED_TEXT
      : ui.state === 'ready' && hasNewTranscriptPart(appointment, note)
        ? `${NEW_TRANSCRIPT_PART_TEXT} — Generate to redraft.`
        : undefined;
  if (!detail) return badge;
  return (
    <Group gap={6} wrap="nowrap">
      {badge}
      <Text size="xs" c="dimmed">
        {detail}
      </Text>
    </Group>
  );
}

export function TranscriptStatusBadge({
  appointment,
  sessionEnded,
  note,
}: TranscriptStatusBadgeProps): JSX.Element | null {
  if (appointment && isChimeTranscriptAppointment(appointment)) {
    return <ChimeTranscriptStatusBadge appointment={appointment} sessionEnded={sessionEnded} note={note} />;
  }
  return null;
}
